import React, { useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import {
	Title,
	TextInput,
	Button,
	Group,
	Table,
	Text,
	Paper,
	Card,
	Box,
	Space,
	SimpleGrid,
	MediaQuery,
	ActionIcon,
	Tooltip,
} from '@mantine/core'
import CameraScanner from '../components/CameraScanner'
import { searchCheckIns } from '../services/actions/checkInActions'
import { getExperienceCheckInProgress } from '../services/actions/experienceActions'
import HistoryIcon from 'tabler-icons-react/dist/icons/history'

const ExperienceSearchPage = () => {
	const { experienceSlug, orgSlug } = useParams()
	const navigate = useNavigate()

	const [query, setQuery] = useState('')
	const [checkIns, setCheckIns] = useState([])
	const [error, setError] = useState('')
	const [hasSearched, setHasSearched] = useState(false)
	const [scannerValue, setScannerValue] = useState('')
	const [checkInProgress, setCheckInProgress] = useState(null)

	const [inputMode, setInputMode] = useState(() => {
		return localStorage.getItem('inputMode') || 'manual'
	})

	const manualInputRef = useRef(null)
	const scannerInputRef = useRef(null)
	const manualSearchTimeoutRef = useRef(null)
	const manualSearchRequestRef = useRef(0)

	useEffect(() => {
		localStorage.setItem('inputMode', inputMode)
	}, [inputMode])

	useEffect(() => {
		if (inputMode === 'manual' && manualInputRef.current) {
			manualInputRef.current.focus()
		}
	}, [inputMode])

	useEffect(() => {
		if (inputMode !== 'manual') {
			clearTimeout(manualSearchTimeoutRef.current)
			manualSearchRequestRef.current += 1
		}
	}, [inputMode])

	useEffect(
		() => () => {
			clearTimeout(manualSearchTimeoutRef.current)
			manualSearchRequestRef.current += 1
		},
		[]
	)

	useEffect(() => {
		let isCurrent = true

		getExperienceCheckInProgress(experienceSlug, orgSlug)
			.then((progress) => {
				if (isCurrent) setCheckInProgress(progress)
			})
			.catch(() => {
				// Progress is informational; check-in search and scanning must remain usable.
			})

		return () => {
			isCurrent = false
		}
	}, [experienceSlug, orgSlug])

	const currentExperience = useSelector(
		(state) => state.experience?.experienceDetails
	)
	const userStation = useSelector(
		(state) => state.experience?.userPosition?.station
	)

	const userStationName = useSelector(
		(state) => state.experience?.userPosition?.stationName
	)

	if (!currentExperience?.id) {
		navigate(`/${orgSlug}/`)
		return null
	}

	const handleSearch = async (e) => {
		e.preventDefault()
		clearTimeout(manualSearchTimeoutRef.current)
		const requestId = ++manualSearchRequestRef.current
		setError('')

		if (query.trim().length < 3) {
			setError('Please enter at least 3 characters.')
			setCheckIns([])
			setHasSearched(false)
			return
		}

		try {
			const results = await searchCheckIns(experienceSlug, orgSlug, query)
			if (requestId !== manualSearchRequestRef.current) return
			setCheckIns(results)
			setHasSearched(true)
		} catch (err) {
			if (requestId !== manualSearchRequestRef.current) return
			console.error(err)
			setError('Search failed. Please try again.')
		}
	}

	const handleSelect = (checkIn) => {
		navigate(`/${orgSlug}/experiences/${experienceSlug}/checkIn/${checkIn.id}`)
	}

	const handleScanSuccess = async (data) => {
		setError('')
		try {
			const results = await searchCheckIns(experienceSlug, orgSlug, data)
			setCheckIns(results)
			setHasSearched(true)
			if (results.length === 1) {
				navigate(
					`/${orgSlug}/experiences/${experienceSlug}/checkIn/${results[0].id}`
				)
			} else {
				setQuery(data)
				setError(results.length === 0 ? 'No results found.' : '')
			}
		} catch (err) {
			console.error(err)
			setError('Scan search failed.')
		}
	}

	const handleInputChange = (e) => {
		const value = e.target.value
		setQuery(value)
		clearTimeout(manualSearchTimeoutRef.current)
		const requestId = ++manualSearchRequestRef.current

		if (value.trim().length < 3) {
			setCheckIns([])
			setHasSearched(false)
			setError('')
			return
		}

		setError('')
		manualSearchTimeoutRef.current = setTimeout(async () => {
			try {
				const results = await searchCheckIns(experienceSlug, orgSlug, value)
				if (requestId !== manualSearchRequestRef.current) return
				setCheckIns(results)
				setHasSearched(true)
				setError(results.length === 0 ? 'No results found.' : '')
			} catch (err) {
				if (requestId !== manualSearchRequestRef.current) return
				console.error(err)
				setError('Auto-search failed.')
			}
		}, 300)
	}

	const alreadyCheckedIn = checkIns.filter((c) => c.checkInStatus === true)
	const activeCheckIns = checkIns.filter((c) => !c.checkInStatus)
	const hasAssignedStation = Boolean(userStation)
	const isUsersStationCheckIn = (checkIn) =>
		checkIn.stationId === userStation || checkIn.station === userStation

	const myStationCheckIns = hasAssignedStation
		? activeCheckIns.filter(isUsersStationCheckIn)
		: activeCheckIns

	const otherStationCheckIns = hasAssignedStation
		? activeCheckIns.filter((c) => !isUsersStationCheckIn(c))
		: []

	const renderCards = (data, statusText) =>
		data.map((b) => (
			<Paper
				withBorder
				p='sm'
				radius='md'
				mb='sm'
				key={b.id}
				style={statusText ? { opacity: 0.7 } : {}}
			>
				<Group position='apart' align='flex-start'>
					<Box>
						<Text fw={600}>{b.travelerName}</Text>
						<Text size='xs' c='dimmed'>
							{b.travelerEmail}
						</Text>
						<Text size='xs' c='dimmed'>
							{b.travelerPhone}
						</Text>
						<Text size='xs'>Date: {b.scheduledCheckInDate?.slice(0, 10)}</Text>
						<Text size='xs'>Station: {b.stationName}</Text>
						{statusText && (
							<Text size='xs' c='green'>
								{statusText}
							</Text>
						)}
					</Box>
					<Button
						size='xs'
						variant={statusText ? 'light' : 'filled'}
						onClick={() => handleSelect(b)}
					>
						{statusText ? 'View' : 'Select'}
					</Button>
				</Group>
			</Paper>
		))

	const renderTable = (data, showStatus) => (
		<Table striped highlightOnHover>
			<thead>
				<tr>
					<th>Name</th>
					<th>Email</th>
					<th>Phone</th>
					<th>Date</th>
					<th>Station</th>
					{showStatus && <th>Status</th>}
					<th></th>
				</tr>
			</thead>
			<tbody>
				{data.map((b) => (
					<tr key={b.id} style={showStatus ? { opacity: 0.6 } : {}}>
						<td>{b.travelerName}</td>
						<td>{b.travelerEmail}</td>
						<td>{b.travelerPhone}</td>
						<td>{b.scheduledCheckInDate?.slice(0, 10)}</td>
						<td>{b.stationName}</td>
						{showStatus && <td>✅ Checked-In</td>}
						<td>
							<Button
								variant={showStatus ? 'light' : 'filled'}
								size='xs'
								onClick={() => handleSelect(b)}
							>
								{showStatus ? 'View' : 'Select'}
							</Button>
						</td>
					</tr>
				))}
			</tbody>
		</Table>
	)

	return (
		<Box px='md' py='md'>
			<Title order={2} mb='md'>
				Check-In Travelers
			</Title>

			{currentExperience?.status === 'inactive' ? (
				'This experience is currently inactive.'
			) : (
				<>
					<Box mb='sm' aria-label='Check-in progress'>
						<SimpleGrid
							cols={4}
							spacing='xs'
							breakpoints={[{ maxWidth: 'sm', cols: 2 }]}
						>
							{[
								[
									'Total Check-Ins Completed',
									checkInProgress?.checkInsCompletedTotal,
								],
								[
									'Total Check-Ins Remaining',
									checkInProgress?.checkInsRemainingTotal,
								],
								[
									'% Complete Overall',
									checkInProgress
										? `${checkInProgress.checkInCompletionRateTotal}%`
										: undefined,
								],
								[
									'My Total Check-Ins Completed',
									checkInProgress?.myCheckInsCompletedTotal,
								],
							].map(([label, value]) => (
								<Card key={label} shadow='sm' withBorder p='sm'>
									<Text size='xs' weight={500}>
										{label}
									</Text>
									<Title order={3}>{value ?? '—'}</Title>
								</Card>
							))}
						</SimpleGrid>
					</Box>

					<Group spacing='xs' mb='sm'>
						{inputMode !== 'cameraScan' && (
							<Button
								variant={inputMode === 'deviceScan' ? 'filled' : 'light'}
								color={inputMode === 'deviceScan' ? 'red' : 'blue'}
								onClick={() => {
									setInputMode(
										inputMode === 'deviceScan' ? 'manual' : 'deviceScan'
									)
									setScannerValue('')
									setError('')
									setCheckIns([])
									setHasSearched(false)
								}}
							>
								{inputMode === 'deviceScan'
									? 'Disable Scan Mode'
									: 'Scan with Device'}
							</Button>
						)}

						{inputMode !== 'deviceScan' && (
							<Button
								variant={inputMode === 'cameraScan' ? 'filled' : 'light'}
								color={inputMode === 'cameraScan' ? 'red' : 'blue'}
								onClick={() => {
									setInputMode(
										inputMode === 'cameraScan' ? 'manual' : 'cameraScan'
									)
									setScannerValue('')
									setError('')
									setCheckIns([])
									setHasSearched(false)
								}}
							>
								{inputMode === 'cameraScan'
									? 'Disable Scan Mode'
									: 'Scan with Camera'}
							</Button>
						)}
						<Tooltip label='View My Check-Ins History'>
							<ActionIcon
								variant='light'
								color='blue'
								size='lg'
								onClick={() =>
									navigate(`/${orgSlug}/experiences/${experienceSlug}/history`)
								}
							>
								<HistoryIcon size={20} />
							</ActionIcon>
						</Tooltip>
					</Group>

					{inputMode === 'deviceScan' && (
						<TextInput
							ref={scannerInputRef}
							value={scannerValue}
							onChange={(e) => setScannerValue(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === 'Enter' && scannerValue.trim()) {
									navigate(
										`/${orgSlug}/experiences/${experienceSlug}/checkIn/${scannerValue.trim()}`
									)
								}
							}}
							autoFocus
							label='Scanner Input (Enter to Check-in)'
							placeholder='Scan check-in ID here'
							w='100%'
						/>
					)}

					{inputMode === 'manual' && (
						<form onSubmit={handleSearch}>
							<Group align='flex-end' spacing='xs' noWrap>
								<TextInput
									ref={manualInputRef}
									label='Search'
									placeholder='Name, Email, Booking Number, or Phone'
									value={query}
									onChange={handleInputChange}
									w='100%'
								/>
								<Button type='submit' disabled={query.trim().length < 3}>
									Search
								</Button>
							</Group>
						</form>
					)}

					{inputMode === 'cameraScan' && (
						<CameraScanner
							onScanSuccess={handleScanSuccess}
							onScanFailure={(err) => console.error('Scan error:', err)}
							onClose={() => setInputMode('manual')}
						/>
					)}

					{error && (
						<Text color='red' mt='sm'>
							{error}
						</Text>
					)}
					<Space h='md' />

					{inputMode === 'manual' &&
						hasSearched &&
						checkIns.length === 25 && (
							<Text size='sm' color='dimmed' mb='sm'>
								Only showing top 25 results
							</Text>
						)}

					{hasSearched && checkIns.length === 0 && !error && (
						<Text>No results found.</Text>
					)}

					{checkIns.length > 0 && (
						<>
							<Title order={4}>
								{hasAssignedStation
									? `My Station: ${userStationName || userStation}`
									: 'All Stations'}
							</Title>
							<MediaQuery largerThan='sm' styles={{ display: 'none' }}>
								<Box>{renderCards(myStationCheckIns)}</Box>
							</MediaQuery>
							<MediaQuery smallerThan='sm' styles={{ display: 'none' }}>
								<Box>
									<Paper withBorder p='sm' mt='xs'>
										{renderTable(myStationCheckIns)}
									</Paper>
								</Box>
							</MediaQuery>

							{otherStationCheckIns.length > 0 && hasAssignedStation && (
								<>
									<Title order={5} mt='lg'>
										Other Check-In Stations
									</Title>
									<MediaQuery largerThan='sm' styles={{ display: 'none' }}>
										<Box>{renderCards(otherStationCheckIns)}</Box>
									</MediaQuery>
									<MediaQuery smallerThan='sm' styles={{ display: 'none' }}>
										<Box>
											<Paper withBorder p='sm' mt='xs'>
												{renderTable(otherStationCheckIns)}
											</Paper>
										</Box>
									</MediaQuery>
								</>
							)}

							{alreadyCheckedIn.length > 0 && (
								<>
									<Title order={5} mt='lg'>
										Already Checked-In
									</Title>
									<MediaQuery largerThan='sm' styles={{ display: 'none' }}>
										<Box>{renderCards(alreadyCheckedIn, '✅ Checked-In')}</Box>
									</MediaQuery>
									<MediaQuery smallerThan='sm' styles={{ display: 'none' }}>
										<Box>
											<Paper withBorder p='sm' mt='xs'>
												{renderTable(alreadyCheckedIn, true)}
											</Paper>
										</Box>
									</MediaQuery>
								</>
							)}
						</>
					)}
				</>
			)}
		</Box>
	)
}

export default ExperienceSearchPage
